import React from 'react'
import { TextClamp } from '../components/text/TextClamp'

export interface SlideHeaderChromeProps {
  eyebrowText?: string
  clientLogoUrl?: string | null
  eyebrowTextTransform?: 'uppercase' | 'none'
  /** Optional node after the eyebrow, kept out of the collapsed eyebrow string. */
  eyebrowSuffix?: React.ReactNode
  /** Optional header control (e.g. a filter). Height-capped at the logo; does not raise chrome. */
  controls?: React.ReactNode
}

export function SlideHeaderChrome(props: SlideHeaderChromeProps) {
  const {
    eyebrowText,
    clientLogoUrl,
    eyebrowTextTransform = 'uppercase',
    eyebrowSuffix,
    controls,
  } = props

  const normalizedEyebrowText = (eyebrowText || '').replace(/\s+/g, ' ').trim()
  const hasControls = Boolean(controls)
  const hasChrome = Boolean(normalizedEyebrowText || clientLogoUrl || hasControls)

  if (!hasChrome) return null

  return (
    <div
      data-testid="slide-header-chrome"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: `0 var(--ds-slide-pad-x)`,
        padding: `var(--ds-eyebrow-pad-y) 0 10px`,
        boxSizing: 'border-box',
        minHeight: 'calc(var(--ds-eyebrow-pad-y) + var(--ds-eyebrow-logo-h) + 10px + var(--ds-border-thin))',
        borderBottom: `var(--ds-border-thin) solid var(--ds-border-color)`,
      }}
    >
      {normalizedEyebrowText && eyebrowSuffix ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            marginRight: hasControls || clientLogoUrl ? '16px' : undefined,
          }}
        >
          <TextClamp
            as="span"
            minLines={1}
            maxLines={1}
            lineHeight="var(--ds-lh-snug)"
            style={{
              fontSize: 'var(--ds-fs-key-findings-eyebrow, var(--ds-fs-body-sm))',
              color: 'var(--ds-text-key-findings-eyebrow, var(--ds-text-muted))',
              fontWeight: 'var(--ds-weight-medium, var(--ds-weight-regular))',
              textTransform: eyebrowTextTransform === 'none' ? 'none' : ('uppercase' as const),
              letterSpacing:
                eyebrowTextTransform === 'none' ? 'var(--ds-tracking-normal)' : 'var(--ds-tracking-wide)',
              flex: '0 1 auto',
              minWidth: 0,
            }}
          >
            {normalizedEyebrowText}
          </TextClamp>
          <span style={{ marginLeft: 12, flexShrink: 0 }}>{eyebrowSuffix}</span>
        </div>
      ) : normalizedEyebrowText ? (
        <TextClamp
          as="span"
          minLines={1}
          maxLines={1}
          lineHeight="var(--ds-lh-snug)"
          style={{
            fontSize: 'var(--ds-fs-key-findings-eyebrow, var(--ds-fs-body-sm))',
            color: 'var(--ds-text-key-findings-eyebrow, var(--ds-text-muted))',
            fontWeight: 'var(--ds-weight-medium, var(--ds-weight-regular))',
            textTransform: eyebrowTextTransform === 'none' ? 'none' : ('uppercase' as const),
            letterSpacing:
              eyebrowTextTransform === 'none' ? 'var(--ds-tracking-normal)' : 'var(--ds-tracking-wide)',
            flex: 1,
            minWidth: 0,
            marginRight: hasControls || clientLogoUrl ? '16px' : undefined,
          }}
        >
          {normalizedEyebrowText}
        </TextClamp>
      ) : null}
      {hasControls ? (
        <div
          data-testid="slide-header-chrome-controls"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            height: 'var(--ds-eyebrow-logo-h)',
            maxHeight: 'var(--ds-eyebrow-logo-h)',
            overflow: 'visible',
          }}
        >
          {controls}
        </div>
      ) : null}
      {clientLogoUrl && (
        // Podium and Exec Summary share this box via SlideHeaderChrome (D9).
        // The logo is decoration, so its alt is empty: an image that fails to
        // load then shows nothing, instead of the English word "Logo" on the slide.
        <img
          src={clientLogoUrl}
          alt=""
          style={{
            height: 'var(--ds-eyebrow-logo-h)',
            maxHeight: 'var(--ds-eyebrow-logo-h)',
            maxWidth: 180,
            width: 'auto',
            objectFit: 'contain' as const,
            marginLeft: hasControls ? 16 : undefined,
          }}
        />
      )}
    </div>
  )
}
